import logger from '../log';
import {
  ObjectRequest,
  ObjectResponseMapper,
  ObjectResponse,
  ObjectInterceptor
} from './interfaces';
import Agent, { RequestError, SourceType } from '../webrtc/agent/interfaces';
import { isManifestUrl } from '../common/utils/sourceTypeUtils';
import composeResponseMappers from './composeResponseMappers';
import { InvalidPayload, AgentFetchFailed, WontIntercept } from './errors';
import InterceptorType from './InterceptorType';
import InterceptorName from './InterceptorName';
import isAllowedRangeRequest from './utils/isAllowedRangeRequest';
import ContentConfig from '../config/ContentConfig';

const log = logger.getLogger(__filename);

export default class WebrtcAgentInterceptor implements ObjectInterceptor {
  readonly agent: Agent;
  readonly config: ContentConfig;
  responseMapper: ObjectResponseMapper | undefined;
  readonly interceptorType = InterceptorType.WEBRTC_AGENT;
  readonly interceptorName = InterceptorName.WEBRTC_AGENT;

  constructor(agent: Agent, config: ContentConfig) {
    this.agent = agent;
    this.config = config;
  }

  currentPeerConnections(): number {
    return this.agent.currentPeerConnections();
  }

  isManifestUrl(url: string): boolean {
    return this.agent.isManifestUrl(url) || isManifestUrl(url);
  }

  isMethodGet(method: string): boolean {
    return method.toUpperCase() === 'GET';
  }

  shouldIntercept(request: ObjectRequest): boolean {
    const { headers, method, url } = request;
    return (
      this.agent.initialized &&
      this.isMethodGet(method) &&
      !this.isManifestUrl(url) &&
      isAllowedRangeRequest(this.config.app as string, headers)
    );
  }

  registerResponseMapper(mapper: ObjectResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  async handle(request: ObjectRequest): Promise<ObjectResponse> {
    const { url, objectId } = request;
    if (this.shouldIntercept(request)) {
      try {
        const resp = await this.agent.fetchObject({ url, objectId });
        const { data, node } = resp;
        const { headers, payload } = data;
        // TODO: call #buffer on slice?
        const payloadCopy: ArrayBuffer = payload && payload.slice(0);
        if (payloadCopy && payloadCopy.byteLength > 0) {
          request.timer.stop();
          const body =
            ArrayBuffer.isView(payloadCopy) ||
            (payloadCopy as Uint8Array).buffer
              ? (payloadCopy as Uint8Array).buffer
              : payloadCopy;
          const resp = {
            body,
            headers,
            // TODO : do we need this?
            // length: payloadCopy.byteLength,
            request,
            timer: request.timer,
            status: 200,
            statusText: 'OK',
            url,
            sourceType: SourceType.PEER,
            objectId
          };
          log.debug('peering', payloadCopy.byteLength, {
            response: resp,
            url
          });
          if (this.responseMapper) {
            return Promise.resolve(this.responseMapper(resp));
          } else {
            return Promise.resolve(resp);
          }
        } else {
          log.warn(
            `Received null or empty payload for ${url} from peer ${node.nodeId}`
          );
          return Promise.reject(new InvalidPayload(request));
        }
      } catch (err) {
        const { unDeliverable, error } = err as RequestError;
        if (unDeliverable) {
          const { statusCode, message } = error;
          // To debug errors, see:
          // https://kollective.atlassian.net/wiki/spaces/ECDN11/pages/1572306949/Peering+Kernel+Status+Codes
          log.debug(
            `Marked undeliverable, error code: ${statusCode}, message: ${message}, request:`,
            request
          );
          // undeliverable means it's not "peerable", so get it from the source.
          return Promise.reject(new WontIntercept());
        } else {
          log.warn('Failed to receive from peer.', err);
          return Promise.reject(
            new AgentFetchFailed(request, error.statusCode, error.message)
          );
        }
      }
    } else {
      log.debug('Marked non-peerable, will not intercept', request);
      return Promise.reject(new WontIntercept());
    }
  }
}
